import type { BattleEndReason, Move, PersonalityId, RoundResultView, Stance } from '@shared/protocol/messages';
import { Emitter } from '../../core/Emitter';
import { createBattle, resolveRound, type BattleState, type CombatStats } from '../../combat/engine';
import { chooseAiMove, chooseAiStance } from '../../combat/ai';
import { Rng, randomSeed } from '../../combat/rng';
import { applyEvent, chargeDue, pickEvent, planTell, TWISTS, type RoundPreview, type Tell, type TwistResult } from '../../combat/twists';
import { DICE_MAX_CHARGES, DICE_STREAK, applyBattleDice, rollDie, type DiceRollResult } from '../../combat/dice';
import type { MultiplayerClient } from '../../../networking/MultiplayerClient';
import type { RoomSession } from '../../../networking/RoomSession';
import { t } from '../../../i18n';

/** Round result already mapped so that index 0 is always the local player. */
export interface LocalRound extends RoundResultView {
  /** -1 tie, 0 = I won the round, 1 = opponent won. */
  winner: -1 | 0 | 1;
  /** Single-player page event settled after the round (combat/twists.ts); hp there is final. */
  twist?: TwistResult;
}

export interface BattleEnd {
  /** 'me' | 'them' | 'draw' */
  outcome: 'me' | 'them' | 'draw';
  reason: BattleEndReason;
  ranked: boolean;
  trophies: number;
}

export type DriverEvents = {
  round: LocalRound;
  end: BattleEnd;
  /** Opponent has committed a move for the current round (move stays secret). */
  opponentLocked: number;
  /** Server corrected our view (e.g. after reconnecting). */
  resync: { round: number; hp: [number, number]; combo: [number, number]; cooldown: [number, number]; deadline: number; myLocked: boolean };
  error: string;
  /** Single-player lucky dice was rolled (charges = what is left). */
  dice: DiceRollResult & { charges: number };
  /** Single-player: the monster's tell, the page event and the boss mood for the next round. */
  preview: RoundPreview;
};

export interface BattleDriver extends Emitter<DriverEvents> {
  readonly kind: 'pve' | 'pvp';
  readonly fighters: [CombatStats, CombatStats];
  round: number;
  deadline: number;
  /** Stance cooldown per side (index 0 = me) for the round being chosen. */
  readonly cooldown: [number, number];
  /** Ink per side (index 0 = me); a full gauge unlocks the ultimate. */
  readonly ink: [number, number];
  /** Combo chain per side (index 0 = me). */
  readonly chain: [Move[], Move[]];
  submit(move: Move, stance?: Stance): Promise<void>;
  forfeit(): void;
  dispose(): void;
}

const swap = <T>(seat: 0 | 1, p: readonly [T, T]): [T, T] => (seat === 0 ? [p[0], p[1]] : [p[1], p[0]]);

/**
 * Single-player battle. The monster commits its move and stance at the start
 * of every round from *previous* rounds only — before the player can pick —
 * so it can never react to the player's current choice.
 */
export class PveDriver extends Emitter<DriverEvents> implements BattleDriver {
  readonly kind = 'pve';
  readonly fighters: [CombatStats, CombatStats];
  private state: BattleState;
  private readonly aiRng = new Rng(randomSeed());
  private committed: Move;
  private committedStance: Stance = 'none';
  private history: Move[] = [];
  round = 1;
  deadline = 0;
  /** Lucky dice the hero may roll (see combat/dice.ts). */
  diceCharges = 1;
  /** Dice rolled this battle (for quests). */
  diceRolled = 0;
  /** What the player is told about the coming round (combat/twists.ts). */
  preview: RoundPreview = { tell: null, event: null, rage: false, rageStarted: false };
  private raging = false;
  private lastEventRound = -99;

  constructor(
    hero: CombatStats,
    monster: CombatStats,
    private readonly personality: PersonalityId,
    /** How sharply the monster plays (see aiLevelFor). */
    private readonly aiLevel = 0,
    /** Chapter boss, tower boss floor or weekly boss: charges, gets angry. */
    private readonly boss = false,
  ) {
    super();
    this.fighters = [hero, monster];
    this.state = createBattle(hero, monster, randomSeed());
    this.committed = this.commit();
  }

  get cooldown(): [number, number] {
    return [this.state.stanceCooldown[0], this.state.stanceCooldown[1]];
  }

  get ink(): [number, number] {
    return [this.state.ink[0], this.state.ink[1]];
  }

  get chain(): [Move[], Move[]] {
    return [[...this.state.chain[0]], [...this.state.chain[1]]];
  }

  /** The monster's move and stance for the coming round (fairness: previous rounds only). */
  private commit(): Move {
    const move = chooseAiMove(this.personality, this.history, this.aiRng, this.aiLevel);
    const s = this.state;
    this.committedStance = chooseAiStance(
      this.personality,
      { cooldown: s.stanceCooldown[1], hp: s.hp[1], maxHp: this.fighters[1].maxHp, combo: s.combo[1], foeHp: s.hp[0], foeMaxHp: this.fighters[0].maxHp, ink: s.ink[1] },
      this.aiRng,
    );
    this.preview = this.plan(move);
    return move;
  }

  /** Boss mood, the monster's tell (or charge) and a page event for the coming round. */
  private plan(move: Move): RoundPreview {
    const s = this.state;
    const round = s.round + 1;
    let rageStarted = false;
    if (this.boss && !this.raging && s.hp[1] > 0 && s.hp[1] * 2 <= this.fighters[1].maxHp) {
      this.raging = true;
      rageStarted = true;
      const angry = { ...s.fighters[1], atk: Math.round((s.fighters[1].atk * (100 + TWISTS.rageAtkPercent)) / 100) };
      this.state = { ...s, fighters: [s.fighters[0], angry] };
    }
    let tell: Tell | null;
    if (this.committedStance === 'ultimate') tell = { kind: 'ultimate' };
    else if (this.boss && this.committedStance === 'none' && s.stanceCooldown[1] === 0 && chargeDue(round, this.raging)) {
      this.committedStance = 'focus';
      tell = { kind: 'charge' };
    } else tell = this.committedStance === 'none' ? planTell(move, this.aiLevel, this.boss, this.raging, this.aiRng) : null;
    const event = pickEvent(round, this.lastEventRound, this.aiRng);
    if (event) this.lastEventRound = round;
    return { tell, event, rage: this.raging, rageStarted };
  }

  async submit(move: Move, stance: Stance = 'none'): Promise<void> {
    if (this.state.winner !== null) return;
    const resolved = resolveRound(this.state, move, this.committed, stance, this.committedStance);
    let state = resolved.state;
    const result: LocalRound = resolved.result as LocalRound;
    if (this.preview.event) {
      const tw = applyEvent(state, result, this.preview.event);
      state = tw.state;
      result.twist = tw.result;
    }
    this.state = state;
    this.history.push(move);
    this.round = state.round + 1;
    // Every few round wins in a row earn another lucky die.
    if (result.winner === 0 && state.combo[0] > 0 && state.combo[0] % DICE_STREAK === 0) this.diceCharges = Math.min(DICE_MAX_CHARGES, this.diceCharges + 1);
    this.emit('round', result);
    if (state.winner !== null) {
      this.emit('end', {
        outcome: state.winner === 'draw' ? 'draw' : state.winner === 0 ? 'me' : 'them',
        reason: state.hp[0] === 0 || state.hp[1] === 0 ? 'ko' : 'rounds',
        ranked: false,
        trophies: 0,
      });
      return;
    }
    // Commit next round's move now, before the player sees the new prompt.
    this.committed = this.commit();
    this.emit('preview', this.preview);
  }

  /** Rolls a lucky die between rounds; a big roll can knock the monster out. */
  rollDice(): DiceRollResult | null {
    if (this.state.winner !== null || this.diceCharges <= 0) return null;
    this.diceCharges--;
    this.diceRolled++;
    const res = applyBattleDice(rollDie(), this.state.hp, [this.fighters[0].maxHp, this.fighters[1].maxHp]);
    this.state = { ...this.state, hp: res.hp, winner: res.hp[1] === 0 ? 0 : null };
    this.emit('dice', { ...res, charges: this.diceCharges });
    if (this.state.winner === 0) this.emit('end', { outcome: 'me', reason: 'ko', ranked: false, trophies: 0 });
    return res;
  }

  /** An Ink Strike between rounds (ui/traceGame.ts): extra damage to the monster; it can knock it out. */
  inkStrike(damage: number): [number, number] {
    if (this.state.winner !== null || damage <= 0) return [this.state.hp[0], this.state.hp[1]];
    const hp: [number, number] = [this.state.hp[0], Math.max(0, this.state.hp[1] - damage)];
    this.state = { ...this.state, hp, winner: hp[1] === 0 ? 0 : this.state.winner };
    if (this.state.winner === 0) this.emit('end', { outcome: 'me', reason: 'ko', ranked: false, trophies: 0 });
    return hp;
  }

  forfeit(): void {
    if (this.state.winner !== null) return;
    this.state = { ...this.state, winner: 1 };
    this.emit('end', { outcome: 'them', reason: 'forfeit', ranked: false, trophies: 0 });
  }

  dispose(): void {
    this.clear();
  }
}

/** Online battle: the server resolves everything; we only send moves (and stances). */
export class PvpDriver extends Emitter<DriverEvents> implements BattleDriver {
  readonly kind = 'pvp';
  readonly fighters: [CombatStats, CombatStats];
  round: number;
  deadline: number;
  cooldown: [number, number];
  ink: [number, number];
  chain: [Move[], Move[]];
  private readonly seat: 0 | 1;
  private readonly battleId: string;
  private offs: (() => void)[] = [];

  constructor(private readonly mp: MultiplayerClient, private readonly session: RoomSession) {
    super();
    const b = session.battle;
    if (!b) throw new Error('no online battle in progress');
    this.seat = session.mySeat;
    this.battleId = b.id;
    const toStats = (i: number): CombatStats => {
      const f = b.players[i]!.fighter;
      return { name: f.name, maxHp: f.maxHp, atk: f.atk, def: f.def, luck: f.luck, skill: f.skill };
    };
    this.fighters = this.seat === 0 ? [toStats(0), toStats(1)] : [toStats(1), toStats(0)];
    this.round = b.round;
    this.deadline = b.deadline;
    this.cooldown = swap(this.seat, b.stanceCooldown ?? [0, 0]);
    this.ink = swap(this.seat, b.ink ?? [0, 0]);
    this.chain = swap(this.seat, b.chain ?? [[], []]);

    this.offs.push(
      session.on('round', (m) => {
        if (m.battleId !== this.battleId) return;
        this.round = m.result.round + 1;
        this.deadline = m.deadline;
        // RoomSession has already advanced the cooldowns from the applied stances.
        const cd = this.session.battle?.id === this.battleId ? this.session.battle.stanceCooldown : undefined;
        if (cd) this.cooldown = swap(this.seat, cd);
        const r = this.local(m.result);
        this.ink = r.ink;
        this.chain = r.chain;
        this.emit('round', r);
      }),
      session.on('locked', (m) => {
        if (m.battleId === this.battleId && m.seat !== this.seat) this.emit('opponentLocked', m.round);
      }),
      session.on('battleSync', (b2) => {
        if (b2.id !== this.battleId) return;
        this.round = b2.round;
        this.deadline = b2.deadline;
        this.cooldown = swap(this.seat, b2.stanceCooldown ?? [0, 0]);
        this.ink = swap(this.seat, b2.ink ?? [0, 0]);
        this.chain = swap(this.seat, b2.chain ?? [[], []]);
        this.emit('resync', {
          round: b2.round,
          hp: swap(this.seat, b2.hp),
          combo: swap(this.seat, b2.combo),
          cooldown: this.cooldown,
          deadline: b2.deadline,
          myLocked: b2.locked[this.seat],
        });
      }),
      session.on('end', (m) => {
        if (m.battleId !== this.battleId) return;
        const me = session.playerId ?? '';
        this.emit('end', {
          outcome: m.winnerId === null ? 'draw' : m.winnerId === me ? 'me' : 'them',
          reason: m.reason,
          ranked: m.ranked,
          trophies: m.trophies[me] ?? 0,
        });
      }),
    );
  }

  private local(r: RoundResultView): LocalRound {
    const stances = r.stances ?? ['none', 'none'];
    const ink = r.ink ?? [0, 0];
    const ult = r.ult ?? [0, 0];
    const chain = r.chain ?? [[], []];
    const burst = r.burst ?? null;
    if (this.seat === 0) return { ...r, stances, ink, ult, chain, burst, burstDamage: r.burstDamage ?? 0 } as LocalRound;
    return {
      ...r,
      moves: [r.moves[1], r.moves[0]],
      winner: r.winner === -1 ? -1 : r.winner === 1 ? 0 : 1,
      healed: [r.healed[1], r.healed[0]],
      hp: [r.hp[1], r.hp[0]],
      combo: [r.combo[1], r.combo[0]],
      stances: [stances[1], stances[0]],
      ink: [ink[1], ink[0]],
      ult: [ult[1], ult[0]],
      chain: [chain[1], chain[0]],
      burst: burst === null ? null : burst === 0 ? 1 : 0,
      burstDamage: r.burstDamage ?? 0,
    };
  }

  async submit(move: Move, stance: Stance = 'none'): Promise<void> {
    try {
      // The stance field is omitted when unused (server default: 'none').
      await this.mp.send(stance === 'none' ? { t: 'battle.move', battleId: this.battleId, round: this.round, move } : { t: 'battle.move', battleId: this.battleId, round: this.round, move, stance });
    } catch (err) {
      const msg = (err as { message?: string }).message ?? t('Move rejected');
      this.emit('error', msg);
      throw err;
    }
  }

  forfeit(): void {
    void this.mp.send({ t: 'battle.forfeit', battleId: this.battleId }).catch(() => {});
  }

  dispose(): void {
    for (const off of this.offs) off();
    this.clear();
  }
}
